import {useMemo, useRef, useState, useCallback} from 'react';

import type {Assignable} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {ThumbsDownRegularIcon} from 'shells/icon';

import type {TaskUpdateInput} from 'nutshell-graphql-types';

import {TaskForm} from '../../../../../../ui/core-entity/widgets/tasks-widget/components/task-form';
import type {
    TaskMutations,
    StepMutations,
} from '../../../../../../ui/core-entity/widgets/tasks-widget/types';
import {refetchForEvent} from '../../../../../../ui/lead/refetch-helpers';
import type {LeadUpdateEvent} from '../../../../../../ui/lead/types';

import {PencilOutlineIcon} from '../../icon/svg/pencil-outline-icon';
import {TrashRegularIcon} from '../../icon/svg/trash-regular-icon';
import type {SavedSettingToastState} from '../../saved-setting-toast/saved-setting-toast';

import {BaseRow} from '../base-row/base-row';
import type {ToolbarProps} from '../base-row/base-row';

import {getAutomatedOptions, mapTaskToDefaultTask} from './helpers';
import {TaskRowContent} from './task-row-content';
import {TaskRowIcon} from './task-row-icon';
import {TaskRowPopovers} from './task-row-popovers';
import type {TaskRowItem} from './types';

export type Props = {
    task: TaskRowItem;
    assignables: Assignable[];
    taskMutations: TaskMutations | null | undefined;
    stepMutations: StepMutations | null | undefined;
    setError?: (error?: string | null | undefined) => void;
    isUpdatingTask?: boolean;
    hideCreatorEntity?: boolean;
};

export function TaskRow(props: Props) {
    const {task, taskMutations, stepMutations, setError} = props;
    const [delayReason, setDelayReason] = useState<string>('');
    const [delayDuration, setDelayDuration] = useState<string>('1-delays');
    const [isAssigneePopoverOpen, setIsAssigneePopoverOpen] = useState<boolean>(false);
    const [isDeletingTask, setIsDeletingTask] = useState<boolean>(false);
    const [isUncompletingTask, setIsUncompletingTask] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isSkippingStep, setIsSkippingStep] = useState<boolean>(false);
    const [isUnskippingStep, setIsUnskippingStep] = useState<boolean>(false);
    const [isDelayingStep, setIsDelayingStep] = useState<boolean>(false);
    const [isCompletingTask, setIsCompletingTask] = useState<boolean>(false);
    const [saveToastState, setSaveToastState] = useState<SavedSettingToastState>('initial');

    const assigneeRef = useRef<HTMLElement | null | undefined>(null);
    const checkboxRef = useRef<HTMLElement | null | undefined>(null);
    const deleteButtonRef = useRef<HTMLElement | null | undefined>(null);
    const skipButtonRef = useRef<HTMLElement | null | undefined>(null);
    const unskipButtonRef = useRef<HTMLElement | null | undefined>(null);
    const delayButtonRef = useRef<HTMLElement | null | undefined>(null);

    const isAutomated = task.__typename === 'Step';

    const showUnhelpfulOption =
        task.__typename === 'Task' && task.isAiGenerated === true && !task.isCompleted;

    // has to be declared before the manualOptions since it's a dependency of the useMemo
    const handleMarkUnhelpfulAndDeleteTask = useCallback(() => {
        setSaveToastState('saving');
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        taskMutations &&
            taskMutations
                .onMarkUnhelpfulAndDeleteTask({
                    task: task.id,
                })
                .catch((e) => {
                    setError?.(safelyGetGraphQLErrorMessage(e));
                    setSaveToastState('initial');
                })
                .then(() => {
                    setSaveToastState('complete');
                });
    }, [taskMutations, task.id, setError, setSaveToastState]);

    const manualOptions = useMemo(
        () => [
            ...(showUnhelpfulOption
                ? [
                      {
                          id: 'unhelpful',
                          icon: ThumbsDownRegularIcon,
                          tooltipText: 'Not a helpful task',
                          onClick: () => {
                              handleMarkUnhelpfulAndDeleteTask();
                          },
                      },
                  ]
                : []),
            {
                id: 'edit',
                icon: PencilOutlineIcon,
                tooltipText: 'Edit task',
                onClick: () => {
                    // Delay setting editing state to allow action menu to fully close
                    // and avoid focus conflicts with taskNote
                    setTimeout(() => {
                        setIsEditing(true);
                    }, 100);
                },
            },
            {
                id: 'delete',
                icon: TrashRegularIcon,
                tooltipText: 'Delete task',
                onClick: () => {
                    setIsDeletingTask(true);
                },
                getRef: (ref: HTMLElement | null | undefined) => {
                    deleteButtonRef.current = ref;
                },
            },
        ],
        [showUnhelpfulOption, handleMarkUnhelpfulAndDeleteTask]
    );

    const automatedOptions = getAutomatedOptions(
        task.isSkippable,
        task.isSkipped,
        task.isDelayable,
        task.canBeCompleted,
        task.isCompleted,
        () => {
            setIsSkippingStep(true);
        },
        () => {
            setIsDelayingStep(true);
        },
        () => {
            handleCompletionChange(true);
        },
        () => {
            setIsUnskippingStep(true);
        },
        skipButtonRef,
        unskipButtonRef,
        delayButtonRef
    );

    const toolbarProps: ToolbarProps = useMemo(
        () => ({
            options: isAutomated ? automatedOptions : manualOptions,
            forceShowToolbar:
                isDeletingTask || isSkippingStep || isUnskippingStep || isDelayingStep,
            forceHideToolbar: isEditing,
        }),
        [
            isAutomated,
            isDeletingTask,
            isEditing,
            manualOptions,
            automatedOptions,
            isSkippingStep,
            isUnskippingStep,
            isDelayingStep,
        ]
    );

    const stepDelays = task.__typename === 'Step' ? task.delays : undefined;
    const delayOptions = useMemo(
        () =>
            stepDelays?.map((delay) => ({
                value: delay?.id ? delay.id : '',
                label: delay?.displayLength ? delay.displayLength : '',
            })) ?? [],
        [stepDelays]
    );

    const handleEventTrigger = useCallback((event: LeadUpdateEvent) => {
        if (NutClientConfig.hasLeadPageRedesignM3FF) {
            refetchForEvent(event);
        } else {
            $(window).trigger(Lead.events.LeadBackboneModelDidUpdate);
        }
    }, []);

    const handleCompletionChange = useCallback(
        (isChecked: boolean) => {
            if (!isChecked && task.completedTime) {
                setIsUncompletingTask(true);

                return;
            }
            setSaveToastState('saving');
            if (task.__typename === 'Step') {
                setIsCompletingTask(true);
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                stepMutations &&
                    stepMutations
                        .onUpdateStep({
                            step: task.id,
                            completion: true,
                        })
                        .catch((e) => {
                            setError?.(safelyGetGraphQLErrorMessage(e));
                            setSaveToastState('initial');
                        })
                        .then(() => {
                            handleEventTrigger('stepStatusChange');
                            setSaveToastState('complete');
                        })
                        .finally(() => {
                            setIsCompletingTask(false);
                        });
            } else {
                setIsCompletingTask(true);
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                taskMutations &&
                    taskMutations
                        .onUpdateTask({
                            task: task.id,
                            completion: true,
                        })
                        .catch((e) => {
                            setError?.(safelyGetGraphQLErrorMessage(e));
                            setSaveToastState('initial');
                        })
                        .then(() => {
                            setSaveToastState('complete');
                        })
                        .finally(() => {
                            setIsCompletingTask(false);
                        });
            }
        },
        [
            task.completedTime,
            task.__typename,
            task.id,
            stepMutations,
            setError,
            handleEventTrigger,
            taskMutations,
        ]
    );

    const handleConfirmUncompleteTask = useCallback(() => {
        setIsUncompletingTask(false);
        setSaveToastState('saving');
        if (task.__typename === 'Step') {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            stepMutations &&
                stepMutations
                    .onUpdateStep({
                        step: task.id,
                        completion: false,
                    })
                    .catch((e) => {
                        setSaveToastState('initial');
                        setError?.(safelyGetGraphQLErrorMessage(e));
                    })
                    .then(() => {
                        handleEventTrigger('stepStatusChange');
                        setSaveToastState('complete');
                    });
        } else {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            taskMutations &&
                taskMutations
                    .onUpdateTask({
                        task: task.id,
                        completion: false,
                    })
                    .catch((e) => {
                        setError?.(safelyGetGraphQLErrorMessage(e));
                        setSaveToastState('initial');
                    })
                    .then(() => {
                        setSaveToastState('complete');
                    });
        }
    }, [taskMutations, stepMutations, task.id, task.__typename, setError, handleEventTrigger]);

    const handleConfirmSkipStep = useCallback(() => {
        setIsSkippingStep(false);
        setSaveToastState('saving');
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        stepMutations &&
            stepMutations
                .onSkipStep({
                    step: {
                        id: task.id,
                    },
                })
                .catch((e) => {
                    setError?.(safelyGetGraphQLErrorMessage(e));
                    setSaveToastState('initial');
                })
                .then(() => {
                    handleEventTrigger('stepStatusChange');
                    setSaveToastState('complete');
                });
    }, [task.id, stepMutations, setError, handleEventTrigger]);

    const handleUpdateAssignee = useCallback(
        (newAssigneeId?: string | null) => {
            setIsAssigneePopoverOpen(false);
            setSaveToastState('saving');
            if (task.__typename === 'Step') {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                stepMutations &&
                    stepMutations
                        .onUpdateStep({
                            step: task.id,
                            assignee: newAssigneeId,
                        })
                        .catch((e) => {
                            setError?.(safelyGetGraphQLErrorMessage(e));
                            setSaveToastState('initial');
                        })
                        .then(() => {
                            handleEventTrigger('stepStatusChange');
                            setSaveToastState('complete');
                        });
            } else {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                taskMutations &&
                    taskMutations
                        .onUpdateTask({
                            task: task.id,
                            assignee: newAssigneeId,
                        })
                        .catch((e) => {
                            setError?.(safelyGetGraphQLErrorMessage(e));
                            setSaveToastState('initial');
                        })
                        .then(() => {
                            setSaveToastState('complete');
                        });
            }
        },
        [taskMutations, stepMutations, task.id, task.__typename, setError, handleEventTrigger]
    );

    const handleUpdateTask = useCallback(
        (input: TaskUpdateInput) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            taskMutations &&
                taskMutations
                    .onUpdateTask(input)
                    .catch((e) => {
                        setError?.(safelyGetGraphQLErrorMessage(e));
                    })
                    .finally(() => {
                        setIsEditing(false);
                    });
        },
        [setIsEditing, taskMutations, setError]
    );

    const onDelayPopoverClose = useCallback(() => {
        setIsDelayingStep(false);
        setDelayReason('');
        setDelayDuration((delayOptions[0] && delayOptions[0].value) || '');
    }, [delayOptions]);

    const handleDelayStep = useCallback(() => {
        onDelayPopoverClose();
        setSaveToastState('saving');
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        stepMutations &&
            stepMutations
                .onDelayStep({
                    step: task.id,
                    delay: delayDuration,
                    note: delayReason,
                })
                .catch((e) => {
                    setError?.(safelyGetGraphQLErrorMessage(e));
                    setSaveToastState('initial');
                })
                .then(() => {
                    handleEventTrigger('stepStatusChange');
                    setSaveToastState('complete');
                });
    }, [
        stepMutations,
        task.id,
        delayDuration,
        delayReason,
        onDelayPopoverClose,
        setError,
        handleEventTrigger,
    ]);

    const handleConfirmDeleteTask = useCallback(() => {
        setIsDeletingTask(false);
        setSaveToastState('saving');
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        taskMutations &&
            taskMutations
                .onDeleteTask({
                    task: task.id,
                })
                .catch((e) => {
                    setError?.(safelyGetGraphQLErrorMessage(e));
                    setSaveToastState('initial');
                })
                .then(() => {
                    setSaveToastState('complete');
                });
    }, [taskMutations, task.id, setError]);

    const handleConfirmUnskipStep = useCallback(() => {
        setIsUnskippingStep(false);
        setSaveToastState('saving');
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        stepMutations &&
            stepMutations
                .onUnskipStep({
                    step: {
                        id: task.id,
                    },
                })
                .catch((e) => {
                    setError?.(safelyGetGraphQLErrorMessage(e));
                    setSaveToastState('initial');
                })
                .then(() => {
                    handleEventTrigger('stepStatusChange');
                    setSaveToastState('complete');
                });
    }, [task.id, stepMutations, setError, handleEventTrigger]);

    const handleDueTimeChange = useCallback(
        (dueTime: number) => {
            setSaveToastState('saving');

            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            taskMutations &&
                taskMutations
                    .onUpdateTask({task: task.id, dueTime})
                    .catch((e) => {
                        setError?.(safelyGetGraphQLErrorMessage(e));
                    })
                    .finally(() => {
                        setIsEditing(false);
                        setSaveToastState('complete');
                    });
        },
        [taskMutations, task.id, setError]
    );

    return (
        <div>
            <BaseRow
                type='tasks'
                isHoverable={true}
                overflowHidden={true}
                customIcon={
                    <TaskRowIcon
                        isSkipped={task.isSkipped}
                        isCompleted={task.isCompleted}
                        canBeCompleted={task.canBeCompleted}
                        requirementText={task.requirementText}
                        isAutomated={isAutomated}
                        isAiGenerated={
                            !props.hideCreatorEntity &&
                            task.__typename === 'Task' &&
                            task.isAiGenerated
                        }
                        creatorEntityName={
                            !props.hideCreatorEntity && task.__typename === 'Task'
                                ? task.creatorEntity?.name
                                : undefined
                        }
                        checkboxRef={(ref) => {
                            checkboxRef.current = ref;
                        }}
                        onChange={handleCompletionChange}
                        isUpdating={isCompletingTask || isEditing}
                    />
                }
                title={isEditing ? undefined : task.title}
                titleColor={task.isCompleted ? 'light' : 'dark'}
                titleIsStrikethrough={task.isSkipped}
                saveToastState={saveToastState}
                setSaveToastState={setSaveToastState}
                toolbarProps={toolbarProps}
                backgroundColor={task.isCompleted ? 'green' : 'default'}
            >
                {isEditing && !isAutomated ? (
                    <TaskForm
                        assignables={props.assignables}
                        defaultTask={mapTaskToDefaultTask(task)}
                        onCancel={() => setIsEditing(false)}
                        onUpdate={handleUpdateTask}
                        isSaving={props.isUpdatingTask}
                        assigner={task.__typename === 'Task' ? task.assigner : undefined}
                        assignedTime={task.__typename === 'Task' ? task.assignedTime : undefined}
                    />
                ) : (
                    <TaskRowContent
                        task={task}
                        assignables={props.assignables}
                        getAssigneeRef={(ref) => {
                            assigneeRef.current = ref;
                        }}
                        setIsAssigneePopoverOpen={setIsAssigneePopoverOpen}
                        onUpdateAssignee={handleUpdateAssignee}
                        handleDueTimeChange={handleDueTimeChange}
                        hideCreatorEntity={props.hideCreatorEntity}
                    />
                )}
            </BaseRow>
            <TaskRowPopovers
                task={task}
                checkboxRef={checkboxRef.current}
                assigneePopoverProps={{
                    anchor: assigneeRef.current,
                    assignables: props.assignables,
                    isOpen: isAssigneePopoverOpen,
                    setIsOpen: setIsAssigneePopoverOpen,
                    onSelect: handleUpdateAssignee,
                }}
                uncompleteTaskProps={{
                    isOpen: isUncompletingTask,
                    setIsOpen: setIsUncompletingTask,
                    onUncomplete: handleConfirmUncompleteTask,
                }}
                deleteTaskProps={{
                    isOpen: isDeletingTask,
                    setIsOpen: setIsDeletingTask,
                    onDelete: handleConfirmDeleteTask,
                    ref: deleteButtonRef.current,
                }}
                skipStepProps={{
                    isOpen: isSkippingStep,
                    setIsOpen: setIsSkippingStep,
                    onSkip: handleConfirmSkipStep,
                    ref: skipButtonRef.current,
                }}
                unskipStepProps={{
                    isOpen: isUnskippingStep,
                    setIsOpen: setIsUnskippingStep,
                    onUnskip: handleConfirmUnskipStep,
                    ref: unskipButtonRef.current,
                }}
                delayStepProps={{
                    isOpen: isDelayingStep,
                    options: delayOptions,
                    duration: delayDuration,
                    reason: delayReason,
                    setDuration: setDelayDuration,
                    setReason: setDelayReason,
                    onDelay: handleDelayStep,
                    onClose: onDelayPopoverClose,
                    ref: delayButtonRef.current,
                }}
            />
        </div>
    );
}
